import React, { useEffect, useState } from "react";
import { ACCENT } from "../artistStudio/shared";
import { parentalApi } from "./parentalApi";

// A parent's time rules for one linked teen (sheet 1.2.1, 1.2.3): daily minutes, allowed hours, time zone, alert level, today's use
// and the last 7 days. The server does the counting; this form only sets the rules.
const ZONES = [
  ["America/New_York", "Eastern (New York)"], ["America/Chicago", "Central (Chicago)"], ["America/Denver", "Mountain (Denver)"],
  ["America/Phoenix", "Arizona (Phoenix)"], ["America/Los_Angeles", "Pacific (Los Angeles)"], ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"], ["Asia/Kolkata", "India (Kolkata)"], ["UTC", "UTC"]
];
const ALERTS = [50, 70, 80, 90];
const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return null; } };

// minutes after midnight <-> "HH:MM" for <input type="time">
export const toClock = (m) => (m === null || m === undefined ? "" : `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
export const fromClock = (text) => {
  const hit = /^(\d{1,2}):(\d{2})$/.exec(text || "");
  return hit ? Number(hit[1]) * 60 + Number(hit[2]) : null;
};
const minutesText = (seconds) => `${Math.floor(seconds / 60)} min`;
const dayLabel = (day) => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" });
};
const messageOf = (e, fallback = "Something went wrong. Please try again.") => e?.response?.data?.message || fallback;

function UsageBars({ history, limitMinutes }) {
  const top = Math.max(limitMinutes || 0, ...history.map((h) => h.minutes), 1);
  return (
    <ul className="mt-3 flex items-end gap-2" aria-label="Minutes used in the last 7 days" data-testid="usage-history">
      {[...history].reverse().map((h) => (
        <li key={h.day} className="flex flex-1 flex-col items-center text-xs text-gray-500">
          <span>{h.minutes}</span>
          <span className="mt-1 w-full rounded-t bg-gray-200" style={{ height: `${Math.max(3, Math.round((h.minutes / top) * 64))}px`, backgroundColor: limitMinutes && h.minutes >= limitMinutes ? "#dc2626" : ACCENT, opacity: 0.85 }} />
          <span className="mt-1">{dayLabel(h.day)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function LimitsEditor({ childId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const fill = (res) => {
    setData(res);
    const l = res.limits;
    setForm({
      daily: l.daily_minutes ? String(l.daily_minutes) : "",
      useWindow: l.window_start !== null,
      start: toClock(l.window_start === null ? 420 : l.window_start),
      end: toClock(l.window_end === null ? 1260 : l.window_end),
      timezone: l.has_limits || l.timezone !== "America/New_York" ? l.timezone : (ZONES.some(([z]) => z === browserZone()) ? browserZone() : l.timezone),
      alert: l.alert_percent
    });
  };
  useEffect(() => {
    let live = true;
    parentalApi.limits(childId).then((res) => { if (live) fill(res); }).catch((e) => { if (live) setError(messageOf(e, "We couldn't load the time limits.")); });
    return () => { live = false; };
  }, [childId]);

  const set = (patch) => { setSaved(false); setForm((f) => ({ ...f, ...patch })); };

  const save = async (e) => {
    e.preventDefault();
    setError(""); setSaved(false);
    const daily = form.daily.trim() === "" ? null : Number(form.daily);
    if (daily !== null && (!Number.isInteger(daily) || daily < 15 || daily > 720)) return setError("Daily time must be between 15 and 720 minutes, or empty for no limit.");
    let start = null; let end = null;
    if (form.useWindow) {
      start = fromClock(form.start); end = fromClock(form.end);
      if (start === null || end === null) return setError("Choose a start and an end time.");
      if (start === end) return setError("The start and end times must be different.");
    }
    setBusy(true);
    try {
      fill(await parentalApi.saveLimits(childId, { daily_minutes: daily, window_start: start, window_end: end, timezone: form.timezone, alert_percent: Number(form.alert) }));
      setSaved(true);
    } catch (err) { setError(messageOf(err)); } finally { setBusy(false); }
  };

  if (!form) return <p className="mt-3 text-sm text-gray-500" role={error ? "alert" : "status"}>{error || "Loading time limits…"}</p>;
  const today = data.today;
  const limitMinutes = data.limits.daily_minutes;
  const input = "rounded-lg border border-gray-300 px-3 py-2 text-sm";
  return (
    <form onSubmit={save} className="mt-3 rounded-xl bg-gray-50 p-4" data-testid="limits-editor">
      <p className="text-sm text-gray-700" data-testid="today-use">
        Today: <strong>{minutesText(today.used_seconds)}</strong>{today.limit_seconds ? ` of ${minutesText(today.limit_seconds)}` : " (no daily limit)"}
        {today.locked && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Locked now</span>}
      </p>
      <UsageBars history={data.history} limitMinutes={limitMinutes} />

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-semibold text-gray-800">Daily time (minutes)
          <input type="number" min="15" max="720" inputMode="numeric" value={form.daily} onChange={(e) => set({ daily: e.target.value })} placeholder="No limit" className={`${input} mt-1 block w-full`} aria-label="Daily time in minutes" />
        </label>
        <label className="block text-sm font-semibold text-gray-800">Tell me when this much is used
          <select value={form.alert} onChange={(e) => set({ alert: Number(e.target.value) })} className={`${input} mt-1 block w-full`} aria-label="Alert level">
            {ALERTS.map((a) => <option key={a} value={a}>{a}%</option>)}
          </select>
        </label>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
        <input type="checkbox" checked={form.useWindow} onChange={(e) => set({ useWindow: e.target.checked })} /> Only allow use between set hours
      </label>
      {form.useWindow && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <input type="time" value={form.start} onChange={(e) => set({ start: e.target.value })} className={input} aria-label="Allowed from" />
          <span>to</span>
          <input type="time" value={form.end} onChange={(e) => set({ end: e.target.value })} className={input} aria-label="Allowed until" />
        </div>
      )}
      <label className="mt-4 block text-sm font-semibold text-gray-800">Time zone
        <select value={form.timezone} onChange={(e) => set({ timezone: e.target.value })} className={`${input} mt-1 block w-full sm:w-72`} aria-label="Time zone">
          {(ZONES.some(([z]) => z === form.timezone) ? ZONES : [[form.timezone, form.timezone], ...ZONES]).map(([z, label]) => <option key={z} value={z}>{label}</option>)}
        </select>
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{busy ? "Saving…" : "Save limits"}</button>
        {saved && <span className="text-sm text-green-700" role="status">Saved. Your teen has been told.</span>}
      </div>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
      <p className="mt-3 text-xs text-gray-500">Time is counted by our server while your teen uses the site. Leave the daily time empty and the hours off to remove all limits.</p>
    </form>
  );
}
